import type { Car } from '../types/car.js';
import type { Driver } from '../types/driver.js';
import type { SeasonContext } from '../types/season.js';
import {
  effectiveBonusFromChampionshipStars,
  effectiveCarBonusFromChampionshipStars,
} from './starLadder.js';
import { TrackCellType } from '../types/track.js';

/** Bonus numériques pour une course (pilote + voiture + saison). */
export interface EffectiveRaceBonuses {
  straightLine: number;
  start: number;
  corner: number;
  combativeness: number;
  /** Bonus Joker (plateau) : appliqué sur les cases de la branche tour joker. */
  jokerLap: number;
}

export const ZERO_RACE_BONUSES: EffectiveRaceBonuses = {
  straightLine: 0,
  start: 0,
  corner: 0,
  combativeness: 0,
  jokerLap: 0,
};

export function computeEffectiveRaceBonuses(
  driver: Pick<Driver, 'pilotage' | 'defense'> | null | undefined,
  car: Pick<Car, 'engine' | 'tire'> | null | undefined,
  season: SeasonContext = {}
): EffectiveRaceBonuses {
  const seasonal = season.seasonalBonusesActive !== false;
  const pilotage = driver?.pilotage ?? 0;
  const defense = driver?.defense ?? 0;
  const engine = car?.engine ?? 0;
  const tire = car?.tire ?? 0;
  return {
    straightLine: effectiveCarBonusFromChampionshipStars(engine),
    start: effectiveCarBonusFromChampionshipStars(tire),
    corner: effectiveBonusFromChampionshipStars(pilotage, seasonal),
    combativeness: effectiveBonusFromChampionshipStars(defense, seasonal),
    jokerLap: 0,
  };
}

/**
 * Bonus de déplacement pour une case.
 * L’ancienne case « joker bonus » (type 4) est traitée comme une droite
 * (les circuits chargés sont aussi normalisés via `normalizeCircuitCellTypes`).
 */
export function resolveMoveBonus(
  cellType: TrackCellType,
  pathIdx: number,
  bonuses: EffectiveRaceBonuses,
  onStartLane?: boolean
): number {
  // `?? 0` : un payload partiel (sans `jokerLap`) ne doit jamais produire NaN
  // dans le budget de déplacement — sinon le pion reste figé sur le tour joker.
  if (cellType === TrackCellType.JOKER_LAP) return bonuses.jokerLap ?? 0;
  if (cellType === TrackCellType.STRAIGHT || cellType === TrackCellType.JOKER) {
    return bonuses.straightLine ?? 0;
  }
  if (cellType === TrackCellType.TURN) return bonuses.corner ?? 0;
  if (
    onStartLane ||
    cellType === TrackCellType.START ||
    cellType === TrackCellType.START_LANE ||
    pathIdx === 0
  ) {
    return bonuses.start ?? 0;
  }
  return 0;
}

export function mergeRaceBonuses(
  base: EffectiveRaceBonuses,
  extra: Partial<EffectiveRaceBonuses>
): EffectiveRaceBonuses {
  return {
    straightLine: base.straightLine + (extra.straightLine ?? 0),
    start: base.start + (extra.start ?? 0),
    corner: base.corner + (extra.corner ?? 0),
    combativeness: base.combativeness + (extra.combativeness ?? 0),
    jokerLap: base.jokerLap + (extra.jokerLap ?? 0),
  };
}
